'use client';

import React, { useState, useEffect, useRef } from 'react';
import { LANDING_RESULTS_CALCULATOR_PLANS_CTA_LABEL } from '@/features/home/constants/landing-results-calculator-copy';

const ResultsCalculator: React.FC = () => {
  const [clients, setClients] = useState(50);
  const [ticketValue, setTicketValue] = useState(250);
  const [recoveryRate, setRecoveryRate] = useState(20);
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const calcRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const plansCtaRef = useRef<HTMLDivElement>(null);

  const recoveredClients = Math.round(clients * (recoveryRate / 100));
  const monthlyRevenue = recoveredClients * ticketValue;
  const yearlyRevenue = monthlyRevenue * 12;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const delay = Number(el.dataset.delay || 0);
            setTimeout(() => {
              el.classList.remove('reveal-hidden');
              el.classList.add('reveal-visible');
              if (el === statsRef.current) setAnimated(true);
            }, delay);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    [headerRef.current, calcRef.current, statsRef.current, plansCtaRef.current].forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });

  return (
    <section id="resultados" ref={sectionRef} className="section-padding bg-off-white">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div ref={headerRef} data-delay="0" className="reveal-hidden max-w-2xl mb-16">
          <span className="tag-badge bg-green-100 text-green-700 border border-green-200 mb-5 inline-block">
            💰 Potencial de Receita
          </span>
          <h2 className="font-fraunces text-navy text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] mb-5">
            Quanto você está
            <span className="italic text-green-600"> deixando ir embora?</span>
          </h2>
          <p className="text-text-muted text-lg font-light leading-relaxed">
            Ajuste os números abaixo com a realidade do seu negócio e veja quanto o RetornaAr pode recuperar para você.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Calculator */}
          <div ref={calcRef} data-delay="150" className="reveal-hidden bg-white rounded-2xl p-8 border border-border-light shadow-sm">
            <h3 className="font-fraunces text-navy text-2xl font-medium mb-8">Calculadora de Receita</h3>

            {/* Slider: Clients */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-text-body text-sm font-semibold">Clientes na sua base</label>
                <span className="font-fraunces text-navy text-2xl font-medium">{clients}</span>
              </div>
              <input
                type="range"
                min="10"
                max="100"
                step="5"
                value={clients}
                onChange={(e) => setClients(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #4AB8E0 0%, #4AB8E0 ${((clients - 10) / 90) * 100}%, #E2EAF8 ${((clients - 10) / 90) * 100}%, #E2EAF8 100%)`,
                }}
                aria-label="Número de clientes na base"
              />
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>10</span><span>100</span>
              </div>
            </div>

            {/* Slider: Ticket */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-text-body text-sm font-semibold">Valor médio da manutenção</label>
                <span className="font-fraunces text-navy text-2xl font-medium">{formatCurrency(ticketValue)}</span>
              </div>
              <input
                type="range"
                min="100"
                max="1000"
                step="50"
                value={ticketValue}
                onChange={(e) => setTicketValue(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #4AB8E0 0%, #4AB8E0 ${((ticketValue - 100) / 900) * 100}%, #E2EAF8 ${((ticketValue - 100) / 900) * 100}%, #E2EAF8 100%)`,
                }}
                aria-label="Valor médio da manutenção em reais"
              />
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>R$ 100</span><span>R$ 1.000</span>
              </div>
            </div>

            {/* Slider: Recovery rate */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-text-body text-sm font-semibold">Taxa de retorno estimada</label>
                <span className="font-fraunces text-navy text-2xl font-medium">{recoveryRate}%</span>
              </div>
              <input
                type="range"
                min="10"
                max="90"
                step="5"
                value={recoveryRate}
                onChange={(e) => setRecoveryRate(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #4AB8E0 0%, #4AB8E0 ${((recoveryRate - 10) / 80) * 100}%, #E2EAF8 ${((recoveryRate - 10) / 80) * 100}%, #E2EAF8 100%)`,
                }}
                aria-label="Taxa de retorno estimada dos clientes"
              />
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>10% (conservador)</span><span>90% (otimista)</span>
              </div>
            </div>

            {/* Summary */}
            <div className="p-5 rounded-xl bg-navy/5 border border-navy/15">
              <p className="text-text-muted text-sm mb-1">
                Com <strong className="text-navy">{clients} clientes</strong>, recuperando <strong className="text-navy">{recoveryRate}%</strong>:
              </p>
              <p className="text-text-muted text-sm">
                = <strong className="text-navy font-bold">{recoveredClients} clientes</strong> voltando a cada ciclo de manutenção
              </p>
            </div>
          </div>

          {/* Results — em lg: altura da linha = coluna da calculadora; três cards dividem o espaço */}
          <div
            ref={statsRef}
            data-delay="300"
            className="reveal-hidden space-y-5 lg:flex lg:min-h-0 lg:flex-col lg:gap-5 lg:space-y-0 lg:self-stretch"
          >
            {/* Monthly */}
            <div className="bg-navy rounded-2xl p-8 text-white lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
              <p className="text-white/50 text-sm font-medium uppercase tracking-widest mb-3">Receita Adicional por Mês</p>
              <p className="font-fraunces text-5xl md:text-6xl font-light tracking-tight text-white mb-2">
                {formatCurrency(monthlyRevenue)}
              </p>
              <p className="text-white/50 text-sm">
                {recoveredClients} clientes × {formatCurrency(ticketValue)}
              </p>
              <div className="mt-5 h-1.5 bg-white/10 rounded-full overflow-hidden lg:mt-auto">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-dark to-cyan transition-all duration-700"
                  style={{ width: `${Math.min((monthlyRevenue / 50000) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Yearly */}
            <div className="bg-gradient-to-br from-cyan-dark to-cyan rounded-2xl p-8 text-navy-dark lg:min-h-0 lg:flex-1 lg:flex lg:flex-col">
              <p className="text-navy-dark/60 text-sm font-medium uppercase tracking-widest mb-3">Receita Adicional por Ano</p>
              <p className="font-fraunces text-5xl md:text-6xl font-light tracking-tight text-navy-dark mb-2">
                {formatCurrency(yearlyRevenue)}
              </p>
              <p className="text-navy-dark/60 text-sm">
                Sem contratar mais nenhum cliente novo
              </p>
            </div>

            {/* Context */}
            <div className="bg-white rounded-2xl p-6 border border-border-light lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:justify-center">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                  </svg>
                </div>
                <div>
                  <p className="text-navy font-semibold text-sm mb-1">Esse dinheiro já é seu.</p>
                  <p className="text-text-muted text-sm leading-relaxed">
                    Esses clientes já pagaram por um serviço antes. Eles confiam em você. O RetornaAr te ajuda a lembrar deles para realizar a próxima manutenção.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          ref={plansCtaRef}
          data-delay="400"
          className="reveal-hidden mt-10 flex w-full justify-center"
        >
          <a
            href="#planos"
            className="btn-primary glow-pulse-on-light flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold sm:w-fit sm:shrink-0 sm:gap-3 sm:px-8 sm:py-4 sm:text-base"
            aria-label="Ir para a seção de planos"
          >
            {LANDING_RESULTS_CALCULATOR_PLANS_CTA_LABEL}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ResultsCalculator;